/**
 * The frames the conversation socket adds, in one place.
 *
 * Everything the agent streams — `assistant`, `tool`, `workflow_state`, `done` — is the
 * vocabulary the SSE route emits, and the panel renders both transports through one
 * switch in ask-pendo.store.js, so those names are deliberately absent here. What is
 * named here is what the socket adds because the connection is bidirectional and
 * long-lived.
 *
 * Names follow AG-UI (https://docs.ag-ui.com) where it has an equivalent, so a later
 * move to it is mechanical.
 *
 * The server's half of this list is ai-connect's `src/routes/ask_pendo/frames.py`. Two
 * copies of a wire contract drift, so keep them in step and keep them the same size: a
 * name here that nothing sends is a frame the server will never see.
 */

export const FRAME = Object.freeze({
    // client → server. Between them these carry what AG-UI puts in RunAgentInput,
    // except that ours can arrive again mid-conversation because a reconnect
    // re-bootstraps rather than starting a new run.
    CONVERSATION_INIT: 'conversation_init',
    USER_MESSAGE: 'user_message',

    // Client-generated id on a user_message, used by the server to drop repeats. A
    // request is at-most-once by construction; a socket is not, so the same message
    // can arrive twice from a reconnect replaying its queue, a second tab or a
    // double-click. Only this side knows the two frames are one message.
    MESSAGE_ID: 'message_id',

    // server → client. The canonical history, sent when our copy is behind the
    // server's. AG-UI's MESSAGES_SNAPSHOT, same payload shape.
    MESSAGES_SNAPSHOT: 'messages_snapshot',

    // Keep-alive, driven by the server. No AG-UI equivalent.
    PING: 'ping',
    PONG: 'pong',

    // Local to the browser: raised by the socket itself, never on the wire. The owner
    // receives them through the same callback as everything above, so a switch over
    // inbound frames has to account for them. Underscored to stay visibly distinct
    // from the wire vocabulary.
    CONVERSATION_REFRESHED: 'conversation_refreshed',
    SOCKET_STATE: '_state',
});
